import { useState, useEffect } from "react";
import { gql } from "@apollo/client";
import { getGmxGraphClient } from "lib/subgraph/clients";
import { bigNumberify } from "lib/numbers";
import { BigNumber } from "ethers";
import { VestInfo } from "./types";
import { useChainId } from "../../lib/chains";

export type AccountVestInfo = {
  tier: number;
  totalPlp: BigNumber;
  totalAllocated: BigNumber;
};

export function useAccountVestInfoBySheduleIndex(accountId: string | null | undefined, scheduleIndex: number) {
  const [accountVestInfo, setAccountVestInfo] = useState<AccountVestInfo | null>(null);
  const { chainId } = useChainId();

  useEffect(() => {
    const getVestInfo = async () => {
      const client = getGmxGraphClient(chainId);

      const queryByIndex = gql`
        {
          vestInfo(id: "${accountId?.toLowerCase()}") {
            scheduleInfo(where: { index: ${scheduleIndex} }) {
              tier
            }
          }
        }
      `;

      const queryByIndexRes = await client!.query<{ vestInfo: VestInfo }>({ query: queryByIndex });
      const tier = queryByIndexRes?.data?.vestInfo.scheduleInfo[0].tier;

      const queryByTier = gql`
        {
          vestInfo(id: "${accountId?.toLowerCase()}") {
            scheduleInfo(where: { tier: ${tier} }) {
              tier
              plpAmount
              allocated
            }
          }
        }
      `;

      const queryByTierRes = await client!.query<{ vestInfo: VestInfo }>({ query: queryByTier });
      const vestInfo = queryByTierRes?.data?.vestInfo;

      if (vestInfo) {
        const accountTierInfo = vestInfo.scheduleInfo.reduce(
          (tierInfo, schedule) => {
            const plpAmount = bigNumberify(schedule.plpAmount) as BigNumber;
            const allocated = bigNumberify(schedule.allocated) as BigNumber;

            return {
              tier: tierInfo.tier,
              totalPlp: tierInfo.totalPlp.add(plpAmount),
              totalAllocated: tierInfo.totalAllocated.add(allocated),
            };
          },
          {
            tier,
            totalPlp: bigNumberify(0),
            totalAllocated: bigNumberify(0),
          } as AccountVestInfo
        );

        setAccountVestInfo(accountTierInfo);
      }
    };

    if (accountId) {
      getVestInfo();
    }
  }, [accountId, scheduleIndex, chainId]);

  return { accountVestInfo };
}
